import { Controller } from "@hotwired/stimulus"
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
} from "chart.js"

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend
)

const patternCache = new Map()

const hexToRgba = (hex, alpha = 1) => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const createStripedPattern = (color) => {
  if (patternCache.has(color)) return patternCache.get(color)

  const pattern = document.createElement("canvas")
  const ctx = pattern.getContext("2d")
  pattern.width = 10
  pattern.height = 10

  ctx.fillStyle = hexToRgba(color, 0.15)
  ctx.fillRect(0, 0, pattern.width, pattern.height)

  ctx.strokeStyle = hexToRgba(color, 0.3)
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(0, 0)
  ctx.lineTo(pattern.width, pattern.height)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(pattern.width, 0)
  ctx.lineTo(0, pattern.height)
  ctx.stroke()

  const p = ctx.createPattern(pattern, "repeat")
  patternCache.set(color, p)
  return p
}

const getChartOptions = (isRevenue, currentMonthIndex = -1) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false },
  plugins: {
    legend: {
      display: false,
      labels: { font: { family: "Geist, sans-serif" } },
    },
    tooltip: {
      filter: (tooltipItem, _index, tooltipItems) => {
        const label = tooltipItem.dataset.label || ""
        const value = tooltipItem.parsed.y
        const dataIndex = tooltipItem.dataIndex

        if (value === null || value === undefined) return false
        if (label.includes("(Base)")) return false

        if (label.includes("(Estimated)")) {
          if (currentMonthIndex >= 0 && dataIndex !== currentMonthIndex) return false
        }

        if (label.includes("(Estimated Fill)")) {
          const hasSolid = tooltipItems.some(
            (item) =>
              item.dataset.label &&
              !item.dataset.label.includes("(Estimated)") &&
              !item.dataset.label.includes("(Base)") &&
              !item.dataset.label.includes("(Estimated Fill)") &&
              item.parsed.y !== null &&
              item.parsed.y !== undefined
          )
          if (hasSolid) return false
        }

        return true
      },
      callbacks: {
        label: (context) => {
          let labelStr = context.dataset.label || ""
          if (labelStr.includes("(Estimated)")) {
            labelStr = "Estimated"
          } else if (labelStr.includes("(Estimated Fill)")) {
            labelStr = "Actual"
          } else if (labelStr.includes("(Base)")) {
            labelStr = "Actual"
          } else {
            labelStr = "Actual"
          }

          const value = context.parsed.y
          if (value === null || value === undefined) return null

          const formattedValue = context.chart.canvas.dataset.revenue === "true"
            ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : value

          return `${labelStr}: ${formattedValue}`
        },
      },
    },
  },
  animation: { duration: 300 },
  scales: {
    x: {
      display: true,
      ticks: { color: "#6b7280", font: { family: "Geist, sans-serif" } },
      grid: { color: "#e5e7eb" },
    },
    y: {
      display: true,
      ticks: {
        color: "#6b7280",
        font: { family: "Geist, sans-serif" },
        callback: (value) => {
          return isRevenue
            ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
            : value
        },
      },
      grid: { color: "#e5e7eb" },
    },
  },
})

const buildDatasets = (label, chartData, chartColor, estimatedData, currentMonthFlags) => {
  const currentMonthIndex = currentMonthFlags ? currentMonthFlags.findIndex((flag) => flag === true) : -1
  const hasEstimate =
    currentMonthIndex >= 0 && estimatedData && estimatedData[currentMonthIndex] !== null

  if (!hasEstimate || currentMonthIndex <= 0) {
    return [
      {
        label,
        data: chartData,
        borderColor: chartColor,
        backgroundColor: hexToRgba(chartColor, 0.1),
        tension: 0.4,
        fill: true,
      },
    ]
  }

  const baseDataset = {
    label: `${label} (Base)`,
    data: chartData,
    borderColor: "transparent",
    backgroundColor: hexToRgba(chartColor, 0.1),
    tension: 0.4,
    fill: "origin",
    pointRadius: 0,
    pointHoverRadius: 0,
  }

  const solidData = chartData.map((value, index) =>
    index <= currentMonthIndex - 1 ? value : null
  )

  const solidDataset = {
    label,
    data: solidData,
    borderColor: chartColor,
    backgroundColor: hexToRgba(chartColor, 0.1),
    tension: 0.4,
    fill: false,
  }

  const dashedData = chartData.map((value, index) => {
    if (index === currentMonthIndex - 1) return value
    if (index === currentMonthIndex) return estimatedData[index]
    return null
  })

  const dashedDataset = {
    label: `${label} (Estimated)`,
    data: dashedData,
    borderColor: chartColor,
    backgroundColor: "transparent",
    borderDash: [5, 5],
    borderWidth: 2,
    tension: 0.4,
    fill: false,
    pointRadius: (ctx) => (ctx.dataIndex === currentMonthIndex ? 4 : 0),
    pointHoverRadius: 6,
    pointBackgroundColor: chartColor,
    pointBorderColor: chartColor,
  }

  const fillData = chartData.map((value, index) => {
    if (index === currentMonthIndex) return value
    if (index === currentMonthIndex - 1) return chartData[index]
    return null
  })

  const fillDataset = {
    label: `${label} (Estimated Fill)`,
    data: fillData,
    borderColor: "transparent",
    backgroundColor: createStripedPattern(chartColor),
    tension: 0.4,
    fill: "-1",
    pointRadius: 0,
    pointHoverRadius: 0,
  }

  return [baseDataset, solidDataset, dashedDataset, fillDataset]
}

// Heartbeat dashboard line charts with estimated-current-month overlays.
export default class extends Controller {
  static targets = ["canvas"]

  connect() {
    patternCache.clear()
    this.charts = []

    this.canvasTargets.forEach((canvas) => {
      const chart = this.createChart(canvas)
      if (chart) this.charts.push(chart)
    })
  }

  disconnect() {
    this.charts?.forEach((chart) => chart.destroy())
    this.charts = []
    patternCache.clear()
  }

  createChart(canvas) {
    try {
      const { months, values, color, estimatedValues, isCurrentMonth, label, revenue } = canvas.dataset
      if (!months || !values) return null

      const chartLabels = JSON.parse(months)
      const chartData = JSON.parse(values)
      const chartColor = color || "#000000"
      const isRevenue = revenue === "true"

      const estimatedData = estimatedValues ? JSON.parse(estimatedValues) : null
      const currentMonthFlags = isCurrentMonth ? JSON.parse(isCurrentMonth) : null
      const currentMonthIndex = currentMonthFlags
        ? currentMonthFlags.findIndex((flag) => flag === true)
        : -1

      const datasets = buildDatasets(
        label || "Metric",
        chartData,
        chartColor,
        estimatedData,
        currentMonthFlags
      )

      return new Chart(canvas, {
        type: "line",
        data: { labels: chartLabels, datasets },
        options: getChartOptions(isRevenue, currentMonthIndex),
      })
    } catch (error) {
      console.error("Error creating heartbeat chart:", error)
      return null
    }
  }
}
